/**
 * Actions and the keymap (docs/05). Every input (keyboard, MIDI, buttons) triggers named actions,
 * so bindings are data and the keymap is remappable.
 *
 * The default keymap splits the keyboard in two, like a game: the left hand flies the show and the
 * right hand (usually on the mouse) shapes the look.
 *
 * - Left, performing: WASD moves through the set (A/D presets, W/S banks), Q/E lean through
 *   favorites, R rerolls, F favorites, T taps, Z/X nudge the beat, C is cruise control
 *   (Auto show), V changes one thing now, G freezes, B brakes (blackout), Tab cues a preview and Space
 *   takes it, number keys are the hotbar. Shift is the opposite or bigger move; Alt the extra one.
 * - Right, tweaking the selected layer: [ ] change its visualization, - = the palette, , . pick a
 *   layer (Shift moves it), ; ' fade it, L switches it off, N adds one, Backspace removes it.
 *   Window keys (H, J, K, M, O, I, ?) live here too: they have buttons the mouse can reach.
 */

export type ActionCategory =
  'Presets' | 'Visual' | 'Layers' | 'Beat' | 'Master' | 'Interface' | 'Outputs' | 'Edit';

export interface ActionDef {
  id: string;
  label: string;
  /** A word or two for the keyboard map. */
  short: string;
  category: ActionCategory;
  /** Takes a slot argument (0–9). */
  slot?: boolean;
  /** Active only while held (flash). */
  momentary?: boolean;
}

export const ACTIONS: readonly ActionDef[] = [
  {
    id: 'preset.recall',
    label: 'Recall preset in slot',
    short: 'Recall',
    category: 'Presets',
    slot: true,
  },
  {
    id: 'preset.store',
    label: 'Save scene to slot',
    short: 'Save',
    category: 'Presets',
    slot: true,
  },
  {
    id: 'preset.flash',
    label: 'Flash preset (while held)',
    short: 'Flash',
    category: 'Presets',
    slot: true,
    momentary: true,
  },
  { id: 'preset.next', label: 'Next preset', short: 'Next', category: 'Presets' },
  { id: 'preset.prev', label: 'Previous preset', short: 'Prev', category: 'Presets' },
  { id: 'preset.random', label: 'Random preset', short: 'Random', category: 'Presets' },
  { id: 'bank.next', label: 'Next bank', short: 'Bank +', category: 'Presets' },
  { id: 'bank.prev', label: 'Previous bank', short: 'Bank −', category: 'Presets' },
  { id: 'autopilot.toggle', label: 'Toggle Auto show', short: 'Auto show', category: 'Presets' },
  { id: 'favorite.add', label: 'Favorite what’s on screen', short: 'Fav', category: 'Presets' },
  {
    id: 'favorite.remove',
    label: 'Unfavorite what’s on screen',
    short: 'Unfav',
    category: 'Presets',
  },
  {
    id: 'favorite.toggle',
    label: 'Favorite / unfavorite (toggle)',
    short: 'Fav on/off',
    category: 'Presets',
  },
  { id: 'favorite.next', label: 'Next favorite', short: 'Next fav', category: 'Presets' },
  { id: 'favorite.prev', label: 'Previous favorite', short: 'Prev fav', category: 'Presets' },
  {
    id: 'scene.surprise',
    label: 'Surprise me (random look)',
    short: 'Surprise',
    category: 'Visual',
  },
  {
    id: 'auto.next',
    label: 'Change one thing now (like Auto show)',
    short: 'Next move',
    category: 'Visual',
  },
  {
    id: 'viz.next',
    label: 'Next visualization (selected layer)',
    short: 'Viz +',
    category: 'Layers',
  },
  {
    id: 'viz.prev',
    label: 'Previous visualization (selected layer)',
    short: 'Viz −',
    category: 'Layers',
  },
  {
    id: 'viz.random',
    label: 'Random visualization (selected layer)',
    short: 'Viz ?',
    category: 'Layers',
  },
  { id: 'palette.next', label: 'Next palette', short: 'Palette +', category: 'Layers' },
  { id: 'palette.prev', label: 'Previous palette', short: 'Palette −', category: 'Layers' },
  { id: 'layer.selectUp', label: 'Select the layer above', short: 'Layer ↑', category: 'Layers' },
  { id: 'layer.selectDown', label: 'Select the layer below', short: 'Layer ↓', category: 'Layers' },
  { id: 'layer.moveUp', label: 'Move the selected layer up', short: 'Move ↑', category: 'Layers' },
  {
    id: 'layer.moveDown',
    label: 'Move the selected layer down',
    short: 'Move ↓',
    category: 'Layers',
  },
  {
    id: 'layer.opacityUp',
    label: 'Selected layer more opaque',
    short: 'Opacity +',
    category: 'Layers',
  },
  {
    id: 'layer.opacityDown',
    label: 'Selected layer more transparent',
    short: 'Opacity −',
    category: 'Layers',
  },
  {
    id: 'layer.toggle',
    label: 'Selected layer on / off',
    short: 'Layer on/off',
    category: 'Layers',
  },
  { id: 'layer.add', label: 'Add a layer (random shapes)', short: 'Add layer', category: 'Layers' },
  {
    id: 'layer.remove',
    label: 'Remove the selected layer',
    short: 'Remove layer',
    category: 'Layers',
  },
  { id: 'transport.tap', label: 'Tap tempo', short: 'Tap', category: 'Beat' },
  {
    id: 'transport.resync',
    label: 'Resync beat (downbeat now)',
    short: 'Beat 1',
    category: 'Beat',
  },
  {
    id: 'transport.detect',
    label: 'Beat detection on / off (hold tempo)',
    short: 'Auto/Hold',
    category: 'Beat',
  },
  { id: 'transport.nudgeBack', label: 'Nudge beat earlier', short: 'Nudge −', category: 'Beat' },
  { id: 'transport.nudgeForward', label: 'Nudge beat later', short: 'Nudge +', category: 'Beat' },
  { id: 'transport.half', label: 'Half tempo', short: '½×', category: 'Beat' },
  { id: 'transport.double', label: 'Double tempo', short: '2×', category: 'Beat' },
  { id: 'master.blackout', label: 'Blackout', short: 'Blackout', category: 'Master' },
  { id: 'master.freeze', label: 'Freeze frame', short: 'Freeze', category: 'Master' },
  {
    id: 'source.playPause',
    label: 'Play / pause (fade in preview mode)',
    short: 'Play',
    category: 'Master',
  },
  { id: 'ui.fullscreen', label: 'Fullscreen', short: 'Fullscreen', category: 'Interface' },
  {
    id: 'ui.toggleControls',
    label: 'Show / hide controls',
    short: 'Controls',
    category: 'Interface',
  },
  { id: 'ui.toggleHud', label: 'Performance HUD', short: 'HUD', category: 'Interface' },
  { id: 'ui.performance', label: 'Performance mode', short: 'Perform', category: 'Interface' },
  {
    id: 'ui.livePane',
    label: 'Show / hide the Live pane',
    short: 'Live pane',
    category: 'Interface',
  },
  {
    id: 'ui.strip',
    label: 'Show / hide favorites & banks strip',
    short: 'Strip',
    category: 'Interface',
  },
  { id: 'ui.help', label: 'Help & keyboard shortcuts', short: 'Help', category: 'Interface' },
  {
    id: 'ui.escape',
    label: 'Close dialog / leave performance mode',
    short: 'Esc',
    category: 'Interface',
  },
  { id: 'output.open', label: 'Open an output window', short: 'Output', category: 'Outputs' },
  { id: 'preview.toggle', label: 'Preview mode on/off', short: 'Preview', category: 'Outputs' },
  { id: 'preview.fade', label: 'Take preview live (fade)', short: 'Fade', category: 'Outputs' },
  { id: 'preview.cut', label: 'Take preview live (cut)', short: 'Cut', category: 'Outputs' },
  { id: 'output.identify', label: 'Identify outputs', short: 'Identify', category: 'Outputs' },
  { id: 'output.record', label: 'Start / stop recording', short: 'Record', category: 'Outputs' },
  { id: 'edit.undo', label: 'Undo', short: 'Undo', category: 'Edit' },
  { id: 'edit.redo', label: 'Redo', short: 'Redo', category: 'Edit' },
];

export interface Binding {
  action: string;
  arg?: number;
}

export type Keymap = Record<string, Binding>;

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

export function defaultKeymap(): Keymap {
  const k: Keymap = {};
  DIGITS.forEach((d, i) => {
    k[d] = { action: 'preset.recall', arg: i };
    k[`Shift+${d}`] = { action: 'preset.store', arg: i };
    k[`Alt+${d}`] = { action: 'preset.flash', arg: i };
  });
  Object.assign(k, {
    // ---- Left hand: performing
    Tab: { action: 'preview.toggle' },
    Space: { action: 'source.playPause' },
    'Shift+Space': { action: 'preview.cut' },
    A: { action: 'preset.prev' },
    D: { action: 'preset.next' },
    W: { action: 'bank.next' },
    S: { action: 'bank.prev' },
    Q: { action: 'favorite.prev' },
    E: { action: 'favorite.next' },
    R: { action: 'preset.random' },
    'Shift+R': { action: 'scene.surprise' },
    'Alt+R': { action: 'output.record' },
    F: { action: 'favorite.add' },
    'Shift+F': { action: 'favorite.remove' },
    T: { action: 'transport.tap' },
    'Shift+T': { action: 'transport.resync' },
    'Alt+T': { action: 'transport.detect' },
    G: { action: 'master.freeze' },
    B: { action: 'master.blackout' },
    Z: { action: 'transport.nudgeBack' },
    X: { action: 'transport.nudgeForward' },
    'Shift+Z': { action: 'transport.half' },
    'Shift+X': { action: 'transport.double' },
    C: { action: 'autopilot.toggle' },
    V: { action: 'auto.next' },
    '`': { action: 'ui.toggleHud' },
    Escape: { action: 'ui.escape' },
    'Ctrl+Z': { action: 'edit.undo' },
    'Meta+Z': { action: 'edit.undo' },
    'Ctrl+Shift+Z': { action: 'edit.redo' },
    'Shift+Meta+Z': { action: 'edit.redo' },
    // ---- Right hand: the selected layer and the look
    '[': { action: 'viz.prev' },
    ']': { action: 'viz.next' },
    '\\': { action: 'viz.random' },
    '-': { action: 'palette.prev' },
    '=': { action: 'palette.next' },
    ',': { action: 'layer.selectDown' },
    '.': { action: 'layer.selectUp' },
    'Shift+,': { action: 'layer.moveDown' },
    'Shift+.': { action: 'layer.moveUp' },
    ';': { action: 'layer.opacityDown' },
    "'": { action: 'layer.opacityUp' },
    L: { action: 'layer.toggle' },
    N: { action: 'layer.add' },
    Backspace: { action: 'layer.remove' },
    Enter: { action: 'preview.fade' },
    'Shift+Enter': { action: 'preview.cut' },
    'Alt+Enter': { action: 'ui.fullscreen' },
    // Window keys: each also has a button.
    H: { action: 'ui.toggleControls' },
    J: { action: 'ui.strip' },
    K: { action: 'ui.livePane' },
    M: { action: 'ui.performance' },
    O: { action: 'output.open' },
    I: { action: 'output.identify' },
    'Shift+/': { action: 'ui.help' },
  });
  return k;
}

/** Which hand a key belongs to in the default layout (for the keyboard map). */
export const LEFT_KEYS: readonly string[] = [
  '`',
  '1',
  '2',
  '3',
  '4',
  '5',
  'Tab',
  'Q',
  'W',
  'E',
  'R',
  'T',
  'A',
  'S',
  'D',
  'F',
  'G',
  'Z',
  'X',
  'C',
  'V',
  'B',
  'Space',
  'Escape',
];

const CODE_KEYS: Record<string, string> = {
  Comma: ',',
  Period: '.',
  Slash: '/',
  BracketLeft: '[',
  BracketRight: ']',
  Minus: '-',
  Equal: '=',
  Backquote: '`',
  Semicolon: ';',
  Quote: "'",
  Backslash: '\\',
  Space: 'Space',
};

export interface KeyEventLike {
  key: string;
  code?: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

/**
 * Normalize a key event to a chord like "Shift+1" or "Ctrl+Z". Uses the physical key (code) for
 * letters, digits and punctuation so Shift/Alt don't change the key name across layouts.
 */
export function chordOf(e: KeyEventLike): string | null {
  let key: string | undefined;
  const code = e.code ?? '';
  let m: RegExpExecArray | null;
  if ((m = /^Digit(\d)$/.exec(code)) || (m = /^Numpad(\d)$/.exec(code))) key = m[1];
  else if ((m = /^Key([A-Z])$/.exec(code))) key = m[1];
  else if (CODE_KEYS[code]) key = CODE_KEYS[code];
  else if (e.key === ' ') key = 'Space';
  else if (e.key.length === 1) key = e.key.toUpperCase();
  else key = e.key;
  if (!key || ['Shift', 'Control', 'Alt', 'Meta', 'Unidentified', 'Dead'].includes(key))
    return null;
  const mods: string[] = [];
  if (e.ctrlKey) mods.push('Ctrl');
  if (e.altKey) mods.push('Alt');
  if (e.shiftKey) mods.push('Shift');
  if (e.metaKey) mods.push('Meta');
  return [...mods, key].join('+');
}

/** Shortcuts the browser or OS keeps for itself; the keymap editor refuses them. */
export const RESERVED_CHORDS: readonly string[] = [
  'Ctrl+W',
  'Meta+W',
  'Ctrl+T',
  'Meta+T',
  'Ctrl+N',
  'Meta+N',
  'Meta+Q',
  'Ctrl+Shift+W',
  'Ctrl+Tab',
  'Ctrl+Shift+Tab',
  'Meta+H',
  'Alt+F4',
];

export function isReserved(chord: string): boolean {
  return RESERVED_CHORDS.includes(chord);
}

export function sanitizeKeymap(input: unknown): Keymap {
  if (!input || typeof input !== 'object') return defaultKeymap();
  const out: Keymap = {};
  for (const [chord, b] of Object.entries(input as Record<string, unknown>)) {
    if (!b || typeof b !== 'object' || isReserved(chord)) continue;
    const binding = b as Binding;
    const def = ACTIONS.find((a) => a.id === binding.action);
    if (!def) continue;
    out[chord] = def.slot
      ? { action: def.id, arg: Math.max(0, Math.min(9, Number(binding.arg) || 0)) }
      : { action: def.id };
  }
  return out;
}

/**
 * A saved keymap plus default bindings for actions it has never seen (added in later versions),
 * as long as the default chord is still free. Actions the user deliberately unbound stay unbound
 * because the saved map lists them in `known`.
 */
export function upgradeKeymap(saved: Keymap, known: readonly string[]): Keymap {
  const out = { ...saved };
  for (const [chord, b] of Object.entries(defaultKeymap())) {
    if (known.includes(b.action) || out[chord]) continue;
    out[chord] = b;
  }
  return out;
}

export function describeBinding(b: Binding): string {
  const def = ACTIONS.find((a) => a.id === b.action);
  if (!def) return b.action;
  return def.slot ? `${def.label} ${(b.arg ?? 0) === 9 ? 0 : (b.arg ?? 0) + 1}` : def.label;
}

/** Chords bound to an action (for showing shortcuts next to buttons). */
export function chordsFor(keymap: Keymap, action: string, arg?: number): string[] {
  return Object.entries(keymap)
    .filter(([, b]) => b.action === action && (arg === undefined || b.arg === arg))
    .map(([c]) => c);
}

/** A chord as people read it: "⇧T", "⌘Z", "Alt ↩", "?". */
export function prettyChord(chord: string): string {
  if (chord === 'Shift+/') return '?';
  const parts = chord.split('+');
  const key = parts.pop()!;
  const keyName: Record<string, string> = {
    Enter: '↩',
    Backspace: '⌫',
    Escape: 'Esc',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
  };
  const mod: Record<string, string> = { Meta: '⌘', Ctrl: 'Ctrl ', Alt: 'Alt ', Shift: '⇧' };
  return parts.map((m) => mod[m] ?? `${m} `).join('') + (keyName[key] ?? key);
}

/** " (T)": the first key bound to an action, for tooltips; empty when unbound. */
export function keyHint(keymap: Keymap, action: string): string {
  const chords = chordsFor(keymap, action);
  return chords.length ? ` (${chords.map(prettyChord).slice(0, 2).join(' or ')})` : '';
}
